import { ApiError, Team, useApiCall, useApiEndpoint } from "@jane/lib/src/api";
import { BuddyTeam, PhoneDirectoryEntry } from "@jane/lib/src/api/repositories/buddy-teams-repository";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import { SectionDivider } from "@jane/lib/src/components/content/SectionDivider";
import { SectionHeader } from "@jane/lib/src/components/content/SectionHeader";
import React, { FC, ReactNode, useMemo, useState } from "react";
import { config } from "../../config";
import { useTeamData } from "../../resources/TeamContext";
import { usePermissions } from "../../resources/PermissionsHook";
import { useTeamMap } from "../../components/team-select";
import { Select } from "@jane/lib/src/components/form/Select";
import { Button, IconButton } from "@jane/lib/src/components/form/Button";
import { InputErrors } from "@jane/lib/src/components/form/InputErrors";
import { ErrorBag } from "@jane/lib/src/api/call";
import { Card } from "@jane/lib/src/components/Card";
import { Toggle } from "@jane/lib/src/components/form/Toggle";
import { faEdit, faPlus, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/pro-solid-svg-icons";
import { AddBuddyTeamToGroupModal } from "../../modals/AddBuddyTeamToGroupModal";
import { DeleteBuddyTeamFromGroupModal } from "../../modals/DeleteBuddyTeamFromGroupModal";
import { ConfirmationModal } from "../../modals/ConfirmationModal";
import { EditBuddyTeamModal } from "../../modals/EditBuddyTeamModal";
import { useModal } from "@jane/lib/src/components/layout/ModalProvider";

export default function TelephoneSettingsPage(): ReactNode {
  const { buddyTeams: buddyTeamsApi, teams: teamsApi } = useApiCall(config);
  const { resource: buddyTeams, isLoading, reload } = useApiEndpoint(() => buddyTeamsApi.all());
  // const { resource: buddyTeamOnsTeam, isloading, reload } = useApiEndpoint(() => buddyTeamsApi.)
  const { teams } = useTeamData();
  const { canListTeams, canListAllTeams } = usePermissions();
  const { resource: teamResource } = useApiEndpoint(() => teamsApi.myTeam(), canListTeams);
  const { resource: secondaryTeams } = useApiEndpoint(() => teamsApi.myTeams(), canListTeams);
  const teamMap = useTeamMap(teams, teamResource ?? null, secondaryTeams ?? []);
  const teamOptionsMap: Map<number, [string, number]> = useMemo(() => {
    return new Map([
      ...teamMap
        .map<[Team, number]>((teams) => {
          const team = teams.slice(-1)[0];
          return [team, teams.length];
        })
        .map<[number, [string, number]]>(([team, indentation]) => [team.id, [team.name, indentation]]),
    ]);
  }, [teamMap]);
  const teamFilterOptions = useMemo(() => {
    return Array.from(teamOptionsMap).map(([id, [name, indentation]]: [number, [string, number]]): [string, string] => {
      return [id.toString(), (canListAllTeams ? "\xA0\xA0".repeat(indentation - 1) : "") + name];
    });
  }, [teamOptionsMap, canListAllTeams]);

  return (
    <ContentContainer>
      <PageHeader>Telefooncentrale</PageHeader>

      <p>Instellingen voor de telefooncentrale. Hier kunnen teams aan belgroepen gekoppeld worden.</p>

      <SectionDivider />

      <SectionHeader>Teams & Belgroepen</SectionHeader>

      {buddyTeams && (
        <AddBuddyTeamForm
          buddyTeams={buddyTeams.buddyTeams}
          directory={buddyTeams.directory}
          teamOptions={teamFilterOptions}
          onSaved={() => reload()}
        />
      )}

      {buddyTeams?.buddyTeams.map((buddyTeam, i) => {
        return <BuddyRow buddyTeam={buddyTeam} key={i} teams={teams} buddyTeams={buddyTeams} reload={() => reload()} />;
      })}
    </ContentContainer>
  );
}

const BuddyRow: FC<{
  buddyTeam: BuddyTeam;
  teams: Team[];
  buddyTeams: { buddyTeams: BuddyTeam[]; directory: PhoneDirectoryEntry[] };
  reload: () => void;
}> = ({ buddyTeam, reload, buddyTeams, teams }) => {
  const { buddyTeams: buddyTeamsApi } = useApiCall(config);
  const modal = useModal({
    title: "Weet je het zeker?",
    body: (
      <ConfirmationModal
        text={"Weet je zeker dat je deze belgroep koppeling wilt verwijderen?"}
        onConfirmed={() => del(buddyTeam.id)}
      />
    ),
  });
  const editModal = useModal({
    title: "Email aanpassen",
    body: <EditBuddyTeamModal team={buddyTeam} onFinished={() => reload()} />,
  });
  const addModal = useModal({
    title: "Team toevoegen",
    body: <AddBuddyTeamToGroupModal buddy={buddyTeam} teams={teams} reload={() => reload()} />,
    size: "md",
  });
  const del = async (buddyTeamId: string) => {
    await buddyTeamsApi.del(buddyTeamId);
    reload();
  };
  const callGroupLookupMap =
    buddyTeams?.directory.reduce(
      (acc, entry) => {
        acc[entry.extension] = entry;
        if (entry.number) {
          acc[entry.number] = entry;
        }
        return acc;
      },
      {} as { [extension: string]: PhoneDirectoryEntry },
    ) ?? {};

  const callGroupTarget: PhoneDirectoryEntry | undefined =
    callGroupLookupMap[buddyTeam.target] ?? callGroupLookupMap[buddyTeam.target];

  return (
    <div className={"mt-4"}>
      <Card>
        <div className={"flex items-stretch space-x-4"}>
          <div className={"flex-1"}>
            <div className={"text-sm font-medium"}>Team(s)</div>
            <div className={"flex flex-wrap gap-2 pt-2"}>
              {buddyTeam.ons_teams.length > 0 &&
                buddyTeam.ons_teams.map((onsTeam, i) => {
                  const team = teams.find((t) => t.id === onsTeam.ons_team_id);
                  if (!team) return null;
                  return (
                    <>
                      <TeamPill key={i} team={team} buddyTeam={buddyTeam} reload={reload} />
                    </>
                  );
                })}
              <div className={"cursor-pointer hover:scale-105 transition-all"}>
                <Button
                  type={"secondary"}
                  size={"xs"}
                  text={"Voeg toe"}
                  icon={faPlus}
                  onClick={() => addModal.open()}
                />
              </div>
            </div>
          </div>
          <div className={"flex-1"}>
            <div className={"text-sm font-medium"}>Belgroep</div>
            <div>{`${callGroupTarget != undefined ? `(${callGroupTarget.extension}) ${callGroupTarget.first_name} ${callGroupTarget.last_name}` : "Onbekende Belgroep"}`}</div>
            <div className={"text-sm"}>{buddyTeam.email ?? callGroupTarget?.user_id}</div>
            {buddyTeam.external_telephone && <div className={"text-sm"}>{buddyTeam.external_telephone}</div>}
          </div>
          <div className={"flex items-center space-x-4"}>
            <IconButton onClick={() => editModal.open()} type={"secondary"} size={"sm"} icon={faEdit} />
            <IconButton onClick={() => modal.open()} type={"danger"} size={"sm"} icon={faTimesCircle} />
            <Toggle
              value={buddyTeam.enabled}
              onChange={async (newValue) => {
                await buddyTeamsApi.addOrUpdate(buddyTeam.target, newValue).then(reload);
                reload();
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const TeamPill: FC<{ buddyTeam: BuddyTeam; team: Team; reload: () => void }> = ({ buddyTeam, team, reload }) => {
  const deleteModal = useModal({
    title: "Team verwijderen",
    body: <DeleteBuddyTeamFromGroupModal buddy={buddyTeam} team={team} reload={() => reload()} />,
    size: "md",
  });
  return (
    <div
      className={
        "group flex items-center px-3 py-1 rounded-full bg-brand-primary text-brand-on-primary gap-2 cursor-pointer hover:scale-105 transition-all"
      }
      onClick={() => deleteModal.open()}
    >
      <span>{team.name}</span>
      <div className={"text-xs"}>
        <FontAwesomeIcon icon={faX} className={"opacity-70 group-hover:opacity-100 transition-opacity"} />
      </div>
    </div>
  );
};

const AddBuddyTeamForm: FC<{
  buddyTeams: BuddyTeam[];
  directory: PhoneDirectoryEntry[];
  teamOptions: [string, string][];
  onSaved: () => void;
}> = (props) => {
  const [team, setTeam] = useState<string | null>(null);
  const [callGroup, setCallGroup] = useState<string | null>(null);
  const callGroupOptions = props.directory.map((entry) => [
    entry.extension,
    `(${entry.extension}) ${entry.first_name} ${entry.last_name}`,
  ]) as [string, string][];
  const [errors, setErrors] = useState<ErrorBag>({});
  const { buddyTeams } = useApiCall(config);
  const save = async () => {
    if (!callGroup || !team) return;
    await buddyTeams.addOrUpdate(callGroup, true, team).catch((error) => {
      if (error instanceof ApiError && typeof error.errorBag === "object") {
        setErrors(error.errorBag);
      }
    });
    props.onSaved();
  };
  return (
    <div>
      <div className={"flex items-end space-x-4"}>
        <div className={"flex-1"}>
          <Select
            label={"Team"}
            options={[["-", "-- Selecteer team --"], ...props.teamOptions]}
            value={team === null ? "-" : team}
            onChange={(t) => setTeam(t === "-" ? null : t)}
          />
        </div>
        <div className={"flex-1"}>
          <Select
            label={"Belgroep"}
            options={[["-", "-- Selecteer belgroep --"], ...callGroupOptions]}
            value={callGroup === null ? "-" : callGroup}
            onChange={(g) => setCallGroup(g === "-" ? null : g)}
          />
        </div>
        <Button text={"Toevoegen"} onClick={save} size={"sm"} disabled={team === null} type={"primary"} />
      </div>
      <InputErrors field={"team_id"} errors={errors} />
    </div>
  );
};
